/**
 * Long-idle wake recovery.
 *
 * Symptom on venue tablets: after Android backgrounds the tab for
 * >5 min (screen off, OS memory pressure, user switched to WhatsApp),
 * the app "freezes" on foregrounding — the WS is silently disconnected,
 * the playback heartbeat stalled, the queue view shows stale state. The
 * operator has to hard-refresh to unstick it.
 *
 * Fix: on every `visibilitychange` → visible, if the tab has been
 * hidden > IDLE_THRESHOLD_MS, silently re-hydrate:
 *   1. Poke the axios interceptor so the ConnectivityPill re-checks.
 *   2. Call the caller-supplied `onWake()` — MusicContext uses it to
 *      re-fetch the queue and re-emit a playback heartbeat.
 *   3. Fire a global `app:wake-recovery` event so RemoteBus (WS layer)
 *      can force-reconnect its socket.
 *
 * Zero-op while the tab is short-hidden (<5 min) — that's the normal
 * "tabbed to Instagram for 30 s" case which the browser handles fine.
 */

const IDLE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
const WAKE_EVENT = 'app:wake-recovery';

/**
 * Register a wake handler. Returns an unregister fn.
 *
 * @param {() => (Promise<void> | void)} onWake — called on every wake after long idle.
 */
export function registerWakeHandler(onWake) {
  if (typeof document === 'undefined') return () => {};

  let hiddenAt = document.hidden ? Date.now() : 0;

  const onVisibilityChange = async () => {
    if (document.hidden) {
      hiddenAt = Date.now();
      return;
    }
    // Tab just became visible.
    if (!hiddenAt) return;
    const elapsed = Date.now() - hiddenAt;
    hiddenAt = 0;
    if (elapsed < IDLE_THRESHOLD_MS) return;

    // eslint-disable-next-line no-console
    console.info('[wakeRecovery] resuming after', Math.round(elapsed / 1000), 's hidden');

    // Broadcast so other subsystems (WS bus, connectivity pill) can react.
    try {
      window.dispatchEvent(new CustomEvent(WAKE_EVENT, { detail: { hiddenMs: elapsed } }));
    } catch { /* noop */ }

    try {
      await onWake?.();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[wakeRecovery] handler failed:', err?.message || err);
    }
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  return () => document.removeEventListener('visibilitychange', onVisibilityChange);
}

export const WAKE_RECOVERY_EVENT = WAKE_EVENT;
